import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, MapPin, Hash, Globe, BarChart3, FileSpreadsheet, ArrowRight, Download } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { Product } from '@/hooks/useProducts';
import { getTranslatedField } from '@/lib/translationHelpers';
import { exampleFilesByProduct } from '@/data/exampleFiles';

const categoryIcons: Record<string, React.ElementType> = {
  addresses: MapPin,
  postalCodes: Hash,
  geodata: Globe,
  statistics: BarChart3,
};

interface FileCardProps {
  product: Product;
}

export function FileCard({ product }: FileCardProps) {
  const { t } = useTranslation();
  const { lang = 'sv' } = useParams<{ lang: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [showFileDetails, setShowFileDetails] = useState(false);
  const [showRangeTypes, setShowRangeTypes] = useState(false);
  const [showColumnTypes, setShowColumnTypes] = useState(false);

  const Icon = categoryIcons[product.ws_category] || FileSpreadsheet;

  const hasFileColumns = product.file_columns && product.file_columns.length > 0;
  const hasStartLength = hasFileColumns && product.file_columns!.some(
    (col) => col.start != null || col.length != null
  );
  const isAddressOrPostal = product.ws_category === 'addresses' || product.ws_category === 'postalCodes';
  const productExamples = exampleFilesByProduct[product.slug];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border border-border rounded-lg bg-card overflow-hidden transition-shadow hover:shadow-md">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center gap-4 p-4 text-left">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-secondary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-foreground text-sm sm:text-base">
                {getTranslatedField(product, 'name', lang)}
              </h2>
              {product.sku && (
                <span className="text-xs text-muted-foreground">Art.nr: {product.sku}</span>
              )}
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                {getTranslatedField(product, 'short_description', lang)}
              </p>
            </div>
            <ChevronDown 
              className={cn(
                "w-5 h-5 text-muted-foreground transition-transform flex-shrink-0",
                isOpen && "rotate-180"
              )} 
            />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t border-border">
            <div className="pt-4 space-y-4">
              {/* Full description */}
              {product.full_description && (
                <p className="text-muted-foreground">
                  {getTranslatedField(product, 'full_description', lang)}
                </p>
              )}

              {/* Specifications row */}
              <div className="flex flex-wrap justify-between gap-y-3">
                {/* Available countries */}
                {product.available_countries && product.available_countries.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.availableCountries')}
                    </p>
                    <div className="flex gap-2 items-center">
                      {product.available_countries.map((code) => {
                        const flag: Record<string, string> = { SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮' };
                        const name: Record<string, string> = { SE: 'Sverige', NO: 'Norge', DK: 'Danmark', FI: 'Finland' };
                        const upper = code.toUpperCase();
                        if (!flag[upper]) return null;
                        return (
                          <span key={upper} className="inline-flex items-center gap-1 text-sm" title={name[upper]}>
                            <span className="text-lg">{flag[upper]}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Row count */}
                {product.row_count && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.rows')}
                    </p>
                    <p className="font-medium text-foreground">{product.row_count}</p>
                  </div>
                )}

                {/* File formats */}
                {product.file_format && product.file_format.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.formats')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {product.file_format.map((format) => (
                        <Badge key={format} variant="secondary" className="text-xs">
                          {format}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Encoding (only for non-coordinate products) */}
                {product.encoding && product.spec_type !== 'coordinate_system' && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.encoding')}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {product.encoding?.join(', ')}
                    </Badge>
                  </div>
                )}

                {/* Coordinate systems (for geodata) */}
                {product.coordinate_systems && product.coordinate_systems.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.coordinateSystems')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {product.coordinate_systems.map((system) => (
                        <Badge key={system} variant="outline" className="text-xs">
                          {system}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Example files */}
                {productExamples && productExamples.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dataFiles.specs.exampleFiles')}
                    </p>
                    <div className="flex flex-col gap-0.5">
                      {productExamples.map((file) => (
                        <a
                          key={file.url}
                          href={file.url}
                          download
                          className="inline-flex items-center gap-1 text-xs text-secondary hover:underline"
                        >
                          <Download className="w-3 h-3" />
                          {file.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* File contents - collapsible */}
              {hasFileColumns && (
                <Collapsible open={showFileDetails} onOpenChange={setShowFileDetails}>
                  <div className="border-t border-border pt-3">
                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline cursor-pointer">
                      <ChevronRight
                        className={cn(
                          "w-4 h-4 transition-transform",
                          showFileDetails && "rotate-90"
                        )}
                      />
                      {showFileDetails
                        ? t('dataFiles.specs.hideFileContents')
                        : t('dataFiles.specs.showFileContents')}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="h-8 text-xs">{t('admin.columnName')}</TableHead>
                              <TableHead className="h-8 text-xs">{t('dataFiles.specs.description')}</TableHead>
                              {hasStartLength && (
                                <>
                                  <TableHead className="h-8 text-xs w-20">{t('dataFiles.specs.start')}</TableHead>
                                  <TableHead className="h-8 text-xs w-20">{t('dataFiles.specs.length')}</TableHead>
                                </>
                              )}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {product.file_columns!.map((col, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="py-1.5 text-sm font-medium">{col.name}</TableCell>
                                <TableCell className="py-1.5 text-sm text-muted-foreground">{col.description || '—'}</TableCell>
                                {hasStartLength && (
                                  <>
                                    <TableCell className="py-1.5 text-sm font-mono">{col.start ?? '—'}</TableCell>
                                    <TableCell className="py-1.5 text-sm font-mono">{col.length ?? '—'}</TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              {/* Range Types - for address/postal products */}
              {isAddressOrPostal && (
                <Collapsible open={showRangeTypes} onOpenChange={setShowRangeTypes}>
                  <div className="border-t border-border pt-3">
                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline cursor-pointer">
                      <ChevronRight
                        className={cn(
                          "w-4 h-4 transition-transform",
                          showRangeTypes && "rotate-90"
                        )}
                      />
                      {showRangeTypes
                        ? t('dataFiles.specs.hideRangeTypes')
                        : t('dataFiles.specs.showRangeTypes')}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="h-8 text-xs w-16">{t('dataFiles.specs.rangeCode')}</TableHead>
                              <TableHead className="h-8 text-xs">{t('dataFiles.specs.description')}</TableHead>
                              <TableHead className="h-8 text-xs">{t('dataFiles.specs.example')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {[
                              { code: 'NU', desc: 'Udda gatunummer', example: 'Storgatan 11, 117' },
                              { code: 'NJ', desc: 'Jämna gatunummer', example: 'Storgatan 12, 188' },
                              { code: 'NO', desc: 'Öppen numrering (främst Box)', example: 'Box 3002' },
                              { code: 'NA', desc: 'Äldre, ej fastställda adresser', example: 'Charlottendal 123' },
                              { code: 'AM', desc: 'Mätpunktsadresser (MMMM-SSSS)', example: 'Landsvägen 112-34' },
                              { code: 'AF', desc: 'Fri intervall (andra postoperatörer)', example: 'Box 19000, Port A' },
                              { code: 'AB', desc: 'Adresser utan numrering', example: 'Vårdinge Kyrka' },
                            ].map((row) => (
                              <TableRow key={row.code}>
                                <TableCell className="py-1.5 text-sm font-mono font-medium">{row.code}</TableCell>
                                <TableCell className="py-1.5 text-sm text-muted-foreground">{row.desc}</TableCell>
                                <TableCell className="py-1.5 text-sm text-muted-foreground italic">{row.example}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              {/* Column Types - for address/postal products */}
              {isAddressOrPostal && (
                <Collapsible open={showColumnTypes} onOpenChange={setShowColumnTypes}>
                  <div className="border-t border-border pt-3">
                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline cursor-pointer">
                      <ChevronRight
                        className={cn(
                          "w-4 h-4 transition-transform",
                          showColumnTypes && "rotate-90"
                        )}
                      />
                      {showColumnTypes
                        ? t('dataFiles.specs.hideColumnTypes')
                        : t('dataFiles.specs.showColumnTypes')}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="h-8 text-xs w-16">{t('dataFiles.specs.rangeCode')}</TableHead>
                              <TableHead className="h-8 text-xs">{t('dataFiles.specs.addressType')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {[
                              { code: 'AT', desc: 'Tätort' },
                              { code: 'AL', desc: 'Landsbygd' },
                              { code: 'AO', desc: 'Boxadresser' },
                              { code: 'BO', desc: 'Box via andra operatörer' },
                              { code: 'PO', desc: 'Andra postleverantörer' },
                              { code: 'AS', desc: 'Större företag med eget postnummer' },
                              { code: 'AR', desc: 'Postenheter, sorteringscenter' },
                              { code: 'AN', desc: 'Administrativ post (tävlingspost)' },
                              { code: 'AU', desc: 'Svarspost/frisvar' },
                            ].map((row) => (
                              <TableRow key={row.code}>
                                <TableCell className="py-1.5 text-sm font-mono font-medium">{row.code}</TableCell>
                                <TableCell className="py-1.5 text-sm text-muted-foreground">{row.desc}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              {/* CTA - links to specific product page */}
              <div className="pt-2">
                <Button asChild size="sm">
                  <Link to={`/${lang}/shop/${product.slug}`}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    {t('dataFiles.orderInShop')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
